import React, { useState, useEffect, useMemo } from 'react';
import { Spin } from 'antd';
import ninjaApi from 'store/redux/apis';
import { LoadingOutlined } from '@ant-design/icons';
import {
  trafficRepotByPeriodMetrics,
  EmptyState,
  TrafficRepotByPeriodCard,
} from './components/TrafficRepotByPeriodMetrics';
import TrafficRepotByPeriodChart from './components/TrafficRepotByPeriodChart';
import { useJourneyReport } from 'modules/ReportModule/context/useJourneyReport';

const TrafficRepotByPeriod = () => {
  const { filter } = useJourneyReport();
  const [trafficByperiodQuery, { data = {}, isLoading, isFetching }] =
    ninjaApi.useLazyTrafficByperiodQuery();
  const [activeMetrics, setActiveMetrics] = useState(['sessions']);

  useEffect(() => {
    if (filter?.website_id) {
      trafficByperiodQuery({
        filters: { ...filter },
      }).unwrap();
    }
  }, [filter]);

  const totals = useMemo(() => data?.totals || {}, [data]);

  const toggleMetric = (metric) =>
    setActiveMetrics((prev) =>
      prev.includes(metric) ? prev.filter((m) => m !== metric) : [...prev, metric]
    );

  const parsedData = useMemo(() => {
    if (!data?.data) return [];

    return data.data.map((item) => {
      const parseDuration = (str) => {
        if (!str) return 0;
        const match = str.match(/(\d+)m\s*(\d+)s/);
        if (!match) return 0;
        const [, minutes, seconds] = match.map(Number);
        return minutes * 60 + seconds;
      };

      return {
        ...item,
        avg_session_duration_str: item.avg_session_duration,
        avg_session_duration: parseDuration(item.avg_session_duration),
      };
    });
  }, [data]);

  return (
    <div className='grid gap-2 m-4'>
      <div className='grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] items-center gap-4'>
        {Object.entries(trafficRepotByPeriodMetrics).map(([key, metric]) => (
          <TrafficRepotByPeriodCard
            key={key}
            metricKey={key}
            metric={metric}
            total={totals[key]}
            isActive={activeMetrics.includes(key)}
            onClick={toggleMetric}
            loading={isLoading || isFetching}
          />
        ))}
      </div>

      {isLoading || isFetching ? (
        <Spin
          indicator={
            <LoadingOutlined spin className='text-color-xlight' style={{ fontSize: 48 }} />
          }
          className='flex items-center justify-center w-full h-full'
          style={{ height: '300px' }}
        />
      ) : !activeMetrics?.length ? (
        <EmptyState message='Please select at least one metric to display the chart' />
      ) : !data?.data?.length ? (
        <EmptyState message='No data available for the selected date range' />
      ) : (
        <TrafficRepotByPeriodChart data={parsedData} activeMetrics={activeMetrics} />
      )}
    </div>
  );
};

export default TrafficRepotByPeriod;
